import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api, formatINR } from "@/lib/api";
import { toast } from "sonner";
import { X, RefreshCw, Truck, Copy, ExternalLink } from "lucide-react";

const STATUS = ["pending", "processing", "shipped", "delivered", "cancelled"];
const PAY_STATUS = ["pending", "paid", "failed", "refunded"];

const STATUS_COLORS = {
  pending:    { bg: "rgba(212,184,150,0.18)", border: "rgba(212,184,150,0.5)", text: "#E8C99B" },
  processing: { bg: "rgba(201,120,154,0.18)", border: "rgba(201,120,154,0.5)", text: "#F0B0CB" },
  shipped:    { bg: "rgba(120,160,210,0.18)", border: "rgba(120,160,210,0.5)", text: "#9DC0EE" },
  delivered:  { bg: "rgba(120,200,150,0.18)", border: "rgba(120,200,150,0.5)", text: "#9DDDB4" },
  cancelled:  { bg: "rgba(220,90,90,0.16)",   border: "rgba(220,90,90,0.5)",   text: "#F1A0A0" },
};

const PAY_COLORS = {
  pending:  { bg: "rgba(212,184,150,0.15)", text: "#E8C99B" },
  paid:     { bg: "rgba(120,200,150,0.18)", text: "#9DDDB4" },
  failed:   { bg: "rgba(220,90,90,0.16)",   text: "#F1A0A0" },
  refunded: { bg: "rgba(180,180,180,0.15)", text: "#CFCBD2" },
};

const Pill = ({ value, palette }) => {
  const c = palette[value] || { bg: "rgba(255,255,255,0.06)", text: "#fff" };
  return (
    <span
      className="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-medium uppercase tracking-[0.18em]"
      style={{ background: c.bg, color: c.text, border: c.border ? `1px solid ${c.border}` : "1px solid transparent" }}
    >
      {value}
    </span>
  );
};

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/orders");
      setOrders(r.data);
    } catch { toast.error("Could not load orders"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, payload) => {
    try {
      const { data } = await api.patch(`/admin/orders/${id}`, payload);
      toast.success("Updated");
      setSelected(data);
      load();
    } catch { toast.error("Update failed"); }
  };

  const shipOrder = async (id) => {
    if (!window.confirm("Create a Xpressbees shipment now? This will book the courier, generate an AWB and email the customer.")) return;
    try {
      const { data } = await api.post(`/admin/orders/${id}/ship`);
      toast.success(data.already_shipped ? `Already shipped — AWB ${data.awb}` : `Shipment booked! AWB ${data.awb}`);
      // Reload the selected order
      const fresh = await api.get(`/admin/orders`);
      const updated = fresh.data.find(o => o.id === id);
      if (updated) setSelected(updated);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not book shipment");
    }
  };

  return (
    <div data-testid="admin-orders-page">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <p className="text-[10px] uppercase tracking-[0.28em] text-[#C9789A] mb-2">Operations</p>
          <h1 className="font-hero text-3xl text-cream">Orders</h1>
          <p className="text-sm text-cream/75 mt-1">{orders.length} {orders.length === 1 ? "order" : "orders"} · click any row to view details &amp; update status.</p>
        </div>
        <button
          onClick={load}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs uppercase tracking-[0.22em] text-cream/70 border border-white/10 hover:bg-white/5"
          data-testid="admin-orders-refresh"
        >
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      <div className="bg-white/[0.08] rounded-2xl border border-white/10 overflow-x-auto">
        <table className="w-full text-sm min-w-[820px]">
          <thead>
            <tr className="text-left font-label text-[10px] uppercase tracking-[0.22em] text-cream/65 border-b border-white/10">
              <th className="py-3.5 px-5">Order</th>
              <th>Customer</th>
              <th>Items</th>
              <th>Payment</th>
              <th>Status</th>
              <th className="text-right px-5">Total</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} className="py-10 text-center text-cream/65">Loading…</td></tr>
            )}
            {!loading && orders.length === 0 && (
              <tr><td colSpan={6} className="py-10 text-center text-cream/65">No orders yet.</td></tr>
            )}
            {orders.map(o => (
              <tr
                key={o.id}
                className="border-b border-white/10 last:border-b-0 hover:bg-white/[0.08] cursor-pointer transition-colors"
                onClick={() => setSelected(o)}
                data-testid={`admin-order-${o.order_number}`}
              >
                <td className="py-4 px-5">
                  <p className="font-cormorant text-lg text-cream leading-tight">{o.order_number}</p>
                  <p className="text-xs text-cream/65 mt-0.5">{new Date(o.created_at).toLocaleDateString()}</p>
                </td>
                <td className="text-cream/85">
                  <p className="text-[13px]">{o.customer_email || o.customer_phone || "Guest"}</p>
                  {o.customer_email && o.customer_phone && (
                    <p className="text-xs text-cream/65 mt-0.5">{o.customer_phone}</p>
                  )}
                </td>
                <td className="text-cream/70">{o.items.length}</td>
                <td><Pill value={o.payment_status} palette={PAY_COLORS} /></td>
                <td><Pill value={o.order_status} palette={STATUS_COLORS} /></td>
                <td className="text-right px-5 font-cormorant text-base text-cream">{formatINR(o.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Detail drawer */}
      <AnimatePresence>
        {selected && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 z-40"
              onClick={() => setSelected(null)}
            />
            <motion.aside
              initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="fixed top-0 right-0 z-50 h-full w-[min(640px,94vw)] bg-[#1a0e15] border-l border-white/10 overflow-y-auto"
              data-testid="admin-order-drawer"
            >
              <div className="sticky top-0 bg-[#1a0e15] border-b border-white/10 px-7 py-5 flex items-start justify-between">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.26em] text-[#C9789A] mb-1">Order</p>
                  <h2 className="font-hero text-2xl text-cream">{selected.order_number}</h2>
                  <p className="text-xs text-cream/75 mt-0.5">{new Date(selected.created_at).toLocaleString()}</p>
                </div>
                <button onClick={() => setSelected(null)} className="text-cream/75 hover:text-cream">
                  <X size={20} />
                </button>
              </div>

              <div className="p-7 space-y-6">
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <Card title="Shipping">
                    <p className="text-cream font-cormorant text-lg">{selected.shipping_address?.full_name}</p>
                    <p className="text-cream/70 text-[13px] mt-1">{selected.shipping_address?.line1}{selected.shipping_address?.line2 ? `, ${selected.shipping_address.line2}` : ""}</p>
                    <p className="text-cream/70 text-[13px]">{selected.shipping_address?.city}, {selected.shipping_address?.state} {selected.shipping_address?.postal_code}</p>
                    <p className="text-cream/75 text-[13px] mt-1">{selected.shipping_address?.phone}</p>
                  </Card>
                  <Card title="Payment">
                    <p className="text-cream text-base uppercase tracking-[0.18em]">{selected.payment_method}</p>
                    <div className="space-y-1 mt-3 text-cream/70 text-[13px]">
                      <Row label="Subtotal" value={formatINR(selected.subtotal)} />
                      {selected.discount > 0 && <Row label="Discount" value={`-${formatINR(selected.discount)}`} accent />}
                      <Row label="Shipping" value={formatINR(selected.shipping_fee)} />
                    </div>
                    <p className="font-hero text-2xl text-cream mt-3 pt-3 border-t border-white/10">{formatINR(selected.total)}</p>
                  </Card>
                </div>

                <Card title="Items">
                  {selected.items.map((it, i) => (
                    <div key={i} className="flex items-center justify-between py-2.5 border-b border-white/10 last:border-0">
                      <div className="flex items-center gap-3 min-w-0">
                        {it.image && <img src={it.image} alt={it.name} className="w-10 h-12 object-cover rounded" />}
                        <div className="min-w-0">
                          <p className="text-cream text-sm font-cormorant truncate">{it.name}</p>
                          <p className="text-xs text-cream/65">Qty {it.quantity}{it.size ? ` · ${it.size}` : ""}</p>
                        </div>
                      </div>
                      <span className="text-cream font-cormorant text-base flex-shrink-0">{formatINR(it.subtotal)}</span>
                    </div>
                  ))}
                </Card>

                <div className="grid md:grid-cols-2 gap-3">
                  <SelectField
                    label="Order status" value={selected.order_status}
                    onChange={(v) => updateStatus(selected.id, { order_status: v })}
                    options={STATUS} testId="admin-order-status-select"
                  />
                  <SelectField
                    label="Payment status" value={selected.payment_status}
                    onChange={(v) => updateStatus(selected.id, { payment_status: v })}
                    options={PAY_STATUS}
                  />
                </div>

                {/* Shipping section */}
                <Card title="Shipping (Xpressbees)">
                  {selected.shipment_awb ? (
                    <div className="space-y-3">
                      <div>
                        <p className="text-[10px] uppercase tracking-[0.22em] text-cream/65 mb-1">AWB / Tracking</p>
                        <div className="flex items-center gap-2">
                          <p className="font-mono text-base text-cream" data-testid="admin-order-awb">{selected.shipment_awb}</p>
                          <button
                            onClick={() => { navigator.clipboard.writeText(selected.shipment_awb); toast.success("Copied"); }}
                            className="text-cream/75 hover:text-cream"
                            title="Copy"
                          ><Copy size={12} /></button>
                        </div>
                      </div>
                      {selected.shipment_courier && (
                        <p className="text-xs text-cream/60">Courier: <span className="text-cream">{selected.shipment_courier}</span></p>
                      )}
                      {selected.shipment_label && (
                        <a
                          href={selected.shipment_label} target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#C9789A] hover:underline"
                          data-testid="admin-order-label-link"
                        >
                          Download label <ExternalLink size={11} />
                        </a>
                      )}
                    </div>
                  ) : (
                    <button
                      onClick={() => shipOrder(selected.id)}
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs uppercase tracking-[0.22em] font-medium text-white"
                      style={{ background: "linear-gradient(135deg, #C9789A, #D4B896)" }}
                      data-testid="admin-order-ship"
                    >
                      <Truck size={13} /> Book Xpressbees shipment
                    </button>
                  )}
                </Card>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

const Card = ({ title, children }) => (
  <div className="bg-white/[0.06] border border-white/10 rounded-xl p-5">
    <p className="text-[10px] uppercase tracking-[0.26em] text-cream/65 mb-3">{title}</p>
    {children}
  </div>
);

const Row = ({ label, value, accent }) => (
  <div className="flex justify-between">
    <span>{label}</span>
    <span className={accent ? "text-[#C9789A]" : ""}>{value}</span>
  </div>
);

const SelectField = ({ label, value, onChange, options, testId }) => (
  <div>
    <label className="block text-[10px] uppercase tracking-[0.24em] text-cream/65 mb-2">{label}</label>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-sm text-cream focus:border-[#C9789A] focus:outline-none transition"
      data-testid={testId}
    >
      {options.map((s) => <option key={s} value={s} className="bg-[#1a0e15]">{s}</option>)}
    </select>
  </div>
);

export default AdminOrders;
